/**
 * CRUD de plantillas de importación (Excel → SUENLACE) para facturas RECIBIDAS.
 * Estructura idéntica a `issuedInvoiceMapping.ts` pero con los campos propios de
 * una compra (proveedor + cuenta de gasto). Las plantillas son GLOBALES por
 * asesoría — se definen una vez y sirven para todas las empresas cliente.
 */

import { supabase } from "@/integrations/supabase/client";
import type { SupplierOperationType } from "@/lib/supplierMaster";

export type ReceivedFieldKey =
  | "invoiceDate"
  | "accountingDate"
  | "series"
  | "invoiceNumber"
  | "vatLedgerNumber"
  | "supplierName"
  | "supplierTaxId"
  | "supplierAccount"
  | "taxableBase"
  | "vatRate"
  | "vatAmount"
  | "surchargeRate"
  | "surchargeAmount"
  | "irpfRate"
  | "irpfAmount"
  | "totalAmount"
  | "expenseAccount"
  | "postalCode"
  | "operationType"
  | "paymentMethod";

export const RECEIVED_FIELDS: { key: ReceivedFieldKey; label: string; required: boolean; hint?: string }[] = [
  { key: "invoiceDate",     label: "Fecha de factura",         required: true },
  { key: "accountingDate",  label: "Fecha contable (opcional)", required: false, hint: "Si no se informa se usa la fecha de factura." },
  { key: "series",          label: "Serie",                     required: false },
  { key: "invoiceNumber",   label: "Nº de factura del proveedor", required: true },
  { key: "vatLedgerNumber", label: "Nº registro IVA (orden contable)", required: false, hint: "Si se informa, se usa como nº de documento y nº de factura en A3; el nº del proveedor pasa al concepto." },
  { key: "supplierName",    label: "Proveedor — Nombre",        required: true },
  { key: "supplierTaxId",   label: "Proveedor — NIF",           required: true },
  { key: "supplierAccount", label: "Proveedor — Cuenta (400/410)", required: false, hint: "Si está vacía se usa la cuenta por defecto del perfil." },
  { key: "taxableBase",     label: "Base imponible",            required: true },
  { key: "vatRate",         label: "% IVA",                     required: false, hint: "Si está vacío se infiere de cuota / base." },
  { key: "vatAmount",       label: "Cuota IVA",                 required: true },
  { key: "surchargeRate",   label: "% Recargo equivalencia",    required: false },
  { key: "surchargeAmount", label: "Cuota recargo",             required: false },
  { key: "irpfRate",        label: "% IRPF",                    required: false },
  { key: "irpfAmount",      label: "Cuota IRPF",                required: false },
  { key: "totalAmount",     label: "Total factura",             required: true },
  { key: "expenseAccount",  label: "Cuenta de gasto (6xx)",     required: false, hint: "Si está vacía se usa la cuenta por defecto del perfil." },
  { key: "postalCode",      label: "Código postal proveedor",   required: false },
  { key: "operationType",   label: "Tipo de operación",         required: false, hint: "Texto reconocido: interior, aib, ais, isp, importacion. Si vacío se usa el del perfil." },
  { key: "paymentMethod",   label: "Forma de pago",             required: false, hint: "Texto tipo CAJA / TARJETA / TRANSFERENCIA / GIRO. Se usa para el asiento de pago." },
];

export type ReceivedColumnMap = Partial<Record<ReceivedFieldKey, string>>;

export interface ReceivedDefaults {
  /** Cuenta de proveedor por defecto (400 / 410). */
  defaultSupplierAccount?: string;
  /** Cuenta de gasto por defecto (6xx). */
  defaultExpenseAccount?: string;
  /** Tipo de operación IVA por defecto. */
  defaultOperationType?: SupplierOperationType;
  dateFormat?: "auto" | "dd/mm/yyyy" | "yyyy-mm-dd" | "mm/dd/yyyy";
  decimalSeparator?: "auto" | "," | ".";
  /** Si true, las líneas con total negativo se marcan como abono / rectificativa. */
  negativeMeansRefund?: boolean;
  /** Mapa forma de pago (UPPER sin acentos) → cuenta de tesorería (570/571…). Vacío = no genera asiento de pago para esa forma. */
  paymentAccountMap?: Record<string, string>;
  /** Si true (default), se genera asiento de pago cuando la forma de pago está en el mapa. */
  generatePaymentEntry?: boolean;
}

export interface ReceivedInvoiceMapping {
  id: string;
  organization_id: string;
  name: string;
  erp_label: string;
  sheet_index: number;
  header_row: number;
  column_map: ReceivedColumnMap;
  defaults: ReceivedDefaults;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const TABLE = "received_invoice_mappings" as const;

type SupabaseAny = ReturnType<typeof supabase.from>;
const table = () => (supabase.from(TABLE as unknown as never) as unknown) as SupabaseAny;

const getCurrentOrgId = async (): Promise<string | null> => {
  const { data: userResp } = await supabase.auth.getUser();
  const userId = userResp.user?.id;
  if (!userId) return null;
  const { data } = await supabase
    .from("profiles")
    .select("organization_id")
    .eq("id", userId)
    .maybeSingle();
  return (data as { organization_id: string | null } | null)?.organization_id ?? null;
};

export const loadAllReceivedMappings = async (): Promise<ReceivedInvoiceMapping[]> => {
  const { data, error } = await table().select("*").order("name");
  if (error) {
    console.error("Error loading received invoice mappings:", error);
    return [];
  }
  return (data || []) as ReceivedInvoiceMapping[];
};

export const createReceivedMapping = async (
  payload: Omit<ReceivedInvoiceMapping, "id" | "created_at" | "updated_at" | "created_by" | "organization_id">,
): Promise<{ data?: ReceivedInvoiceMapping; error?: string }> => {
  const { data: userResp } = await supabase.auth.getUser();
  const userId = userResp.user?.id ?? null;
  const orgId = await getCurrentOrgId();
  if (!orgId) return { error: "No se pudo determinar la asesoría del usuario." };
  const { data, error } = await table()
    .insert({ ...payload, organization_id: orgId, created_by: userId })
    .select("*")
    .single();
  if (error) return { error: error.message };
  return { data: data as ReceivedInvoiceMapping };
};

export const updateReceivedMapping = async (
  id: string,
  patch: Partial<Omit<ReceivedInvoiceMapping, "id" | "organization_id" | "created_at" | "updated_at" | "created_by">>,
): Promise<{ error?: string }> => {
  const { error } = await table().update(patch).eq("id", id);
  return { error: error?.message };
};

export const deleteReceivedMapping = async (id: string): Promise<{ error?: string }> => {
  const { error } = await table().delete().eq("id", id);
  return { error: error?.message };
};
